/**
 * 項目ごとの差分の表 (マスタ変更履歴 SBI 7)。③ 履歴詳細と「最新との比較」で共有する。
 *
 * - 項目名は API が返す日本語ラベルだけを出す。内部の `path` は画面に出さない。
 * - 値は {@link formatChangeValue} で整形し、長い文・複数行は折り返して全文を見せる。
 * - 列幅は固定し (styles.css の `.master-history-changes-table`)、横スクロールを出さない。
 * - 追加 / 削除 / 変更は文言で示す (色だけに頼らない)。
 * - 変更前後がともに空でない文字列のときは、変わった部分だけを強調する (`diffTextValues`)。
 *   強調は `<mark>` 要素で、太字と下線も付ける (色だけに頼らない)。値は React の子として描画し、
 *   HTML としては解釈しない。強調の外側 (共通部分) は全文を残す。
 */
import type React from 'react';

import type { MasterHistoryChange } from '../api/types';
import { MASTER_HISTORY_CHANGE_KIND_LABEL } from '../utils/masterHistoryLabels';
import { formatChangeValue } from '../utils/masterHistoryValue';
import { diffTextValues } from '../utils/textDiff';

const NO_LABEL = '(項目名なし)';

const VALUE_CELL_STYLE: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

/** 変わった部分の強調。背景色に加えて太字・下線を付け、色の区別に頼らない */
const MARK_STYLE_BASE: React.CSSProperties = {
  fontWeight: 700,
  textDecoration: 'underline',
  color: 'inherit',
};
const MARK_STYLE_BEFORE: React.CSSProperties = {
  ...MARK_STYLE_BASE,
  background: 'var(--color-danger-bg)',
};
const MARK_STYLE_AFTER: React.CSSProperties = {
  ...MARK_STYLE_BASE,
  background: 'var(--color-success-bg)',
};

/** 値のセル。強調する変更部分があるときだけ `<mark>` で囲む */
function ValueCell(props: {
  value: unknown;
  prefix: string;
  changed: string;
  suffix: string;
  highlight: boolean;
  side: 'before' | 'after';
  testId: string;
}): React.JSX.Element {
  const { value, prefix, changed, suffix, highlight, side, testId } = props;
  if (!highlight) {
    return (
      <td style={VALUE_CELL_STYLE} data-testid={testId}>
        {formatChangeValue(value)}
      </td>
    );
  }
  return (
    <td style={VALUE_CELL_STYLE} data-testid={testId}>
      {prefix}
      {changed !== '' && (
        <mark
          style={side === 'before' ? MARK_STYLE_BEFORE : MARK_STYLE_AFTER}
          data-testid={`${testId}-mark`}
        >
          {changed}
        </mark>
      )}
      {suffix}
    </td>
  );
}

export interface MasterHistoryChangeTableProps {
  changes: readonly MasterHistoryChange[];
  /** 左の値の見出し (例: 変更前 / この版) */
  beforeHeading: string;
  /** 右の値の見出し (例: 変更後 / 最新) */
  afterHeading: string;
  /** `data-testid` の接頭辞 */
  testIdPrefix: string;
}

export function MasterHistoryChangeTable(props: MasterHistoryChangeTableProps): React.JSX.Element {
  const { changes, beforeHeading, afterHeading, testIdPrefix } = props;
  return (
    <div className="table-wrapper">
      <table
        className="data-table master-history-changes-table"
        data-testid={`${testIdPrefix}-table`}
      >
        <colgroup>
          <col className="master-history-changes-table__col--label" />
          <col className="master-history-changes-table__col--kind" />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">項目</th>
            <th scope="col">種類</th>
            <th scope="col">{beforeHeading}</th>
            <th scope="col">{afterHeading}</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change, index) => {
            const diff = diffTextValues(change.before, change.after);
            return (
              <tr
                key={`${change.path ?? ''}#${index}`}
                data-testid={`${testIdPrefix}-row-${index}`}
              >
                <td style={{ whiteSpace: 'normal' }}>{change.label || NO_LABEL}</td>
                <td>{change.kind ? MASTER_HISTORY_CHANGE_KIND_LABEL[change.kind] : '—'}</td>
                <ValueCell
                  value={change.before}
                  prefix={diff?.prefix ?? ''}
                  changed={diff?.beforeChanged ?? ''}
                  suffix={diff?.suffix ?? ''}
                  highlight={diff !== null}
                  side="before"
                  testId={`${testIdPrefix}-before-${index}`}
                />
                <ValueCell
                  value={change.after}
                  prefix={diff?.prefix ?? ''}
                  changed={diff?.afterChanged ?? ''}
                  suffix={diff?.suffix ?? ''}
                  highlight={diff !== null}
                  side="after"
                  testId={`${testIdPrefix}-after-${index}`}
                />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
